import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { Status } from '../../../../../../../shared/dependenciesV2';
import { DISABLE_PACKAGE_LABELS } from '../../../../../../../shared/featureFlags/flagNames';
import Locator from '../../../../../../../shared/Locator';
import { useIsFeatureEnabled } from '../../../../../../helpers/features';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import { getStatusFromDependencyState } from '../helpers';

import { AddAuthentication, EditPackage, IgnorePackage, ManageLabels, OverwritePackage, QueueBuild, ViewBuild } from './ActionItems';
import type { MenuProps } from '.';

export const AnalyzedMenu: VoidFunctionComponent<MenuProps> = ({ dependency, modals, isProjectScope }) => {
  const locator = new Locator(dependency.locator);
  const shouldShowOverwrite = isProjectScope && locator.fetcher.value === 'user';
  const isPackageLabelsDisabled = useIsFeatureEnabled(DISABLE_PACKAGE_LABELS);
  const shouldShowPackageLabels = !isPackageLabelsDisabled && isProjectScope;

  return (
    <>
      <EditPackage
        modals={modals}
        locator={dependency.locator}
      />
      {shouldShowOverwrite && (
        <OverwritePackage
          modals={modals}
          locator={dependency.locator}
        />
      )}
      {shouldShowPackageLabels && (
        <ManageLabels
          modals={modals}
          locator={dependency.locator}
          title={dependency.title}
        />
      )}
      {isProjectScope && (
        <IgnorePackage
          title={dependency.title}
          isManual={dependency.isManual}
          modals={modals}
          locator={dependency.locator}
        />
      )}
    </>
  );
};

export const ErrorDropdown: VoidFunctionComponent<MenuProps> = ({ dependency, modals, isProjectScope }) => (
  <>
    {getStatusFromDependencyState(dependency.status) === Status.Unknown &&
      <AddAuthentication
        locator={dependency.locator}
      />}
    <QueueBuild
      locator={dependency.locator}
    />
    {isProjectScope && (
      <>
        <OverwritePackage
          modals={modals}
          locator={dependency.locator}
        />
        <IgnorePackage
          title={dependency.title}
          isManual={dependency.isManual}
          modals={modals}
          locator={dependency.locator}
        />
      </>
    )}
  </>
);

export const BuildDropdown: VoidFunctionComponent<MenuProps> = ({ dependency, modals, isProjectScope }) => {
  const latestBuild = useSelector(DependenciesV2Store.selectors.getLatestBuildOnRevision(dependency.locator));

  return (
    <>
      {!latestBuild ?
        <QueueBuild locator={dependency.locator} /> :
        <ViewBuild modals={modals} buildId={latestBuild.id} />}
      {isProjectScope && (
        <>
          <OverwritePackage
            modals={modals}
            locator={dependency.locator}
          />
          <IgnorePackage
            title={dependency.title}
            isManual={dependency.isManual}
            modals={modals}
            locator={dependency.locator}
          />
        </>
      )}
    </>
  );
};
